import { useState } from 'react';
import {
  Box,
  Container,
  IconButton,
  Typography,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport,
  GridValueGetterParams,
  GridColDef,
  deDE,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid';

import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

import { observer } from 'mobx-react-lite';

import { computed } from 'mobx';
import { useStore } from '../store/context';
import { RedeemCreditStatus } from '../types/transport';
import { OrderElement, RedeemedPointsWithId } from '../store/PointsStore';

const MaexchenOrdersPage = observer((): JSX.Element => {
  const { userStore, redeemProductStore } = useStore();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [currentRow, setCurrentRow] = useState<null | OrderElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    row: OrderElement,
  ) => {
    setCurrentRow(row);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getOrderItem = (element: RedeemedPointsWithId) => {
    const redeemEntry = redeemProductStore?.entriesArray.find(
      entry => entry.ProduktId === element.productId,
    );

    return `${
      element?.quantity > 1
        ? `${element.quantity}x ${redeemEntry?.Produktname}`
        : `${redeemEntry?.Produktname}`
    }${element.productVariantName ? ` (${element.productVariantName})` : ''}`;
  };

  const updateRow = async (
    selectedRow: OrderElement | null,
    newStatus: RedeemCreditStatus,
  ) => {
    if (selectedRow && selectedRow.products.length) {
      try {
        await Promise.all(
          selectedRow.products.map(async product => {
            await product.setStatus(newStatus);
          }),
        );
      } catch (error) {
        console.log(error);
      }
    }

    handleClose();
  };

  const creditStatusTranslation: Record<RedeemCreditStatus, string> = {
    OPEN: 'Offen',
    PROCESSING: 'In Bearbeitung',
    PROCESSING_EXTERNAL: 'In Bearbeitung Extern',
    DONE: 'Abgeschlossen',
    CANCELLED: 'Storniert',
  };

  const allOrders = computed(() =>
    (userStore?.allUserOrders ?? []).map(item => ({
      ...item,
      orderProductsString: item.products.map(getOrderItem).join(' / '),
    })),
  ).get();

  // defining the grid columns
  const columns: GridColDef<typeof allOrders[0]>[] = [
    {
      field: 'timestamp',
      headerName: 'Datum',
      width: 150,
      type: 'date',
      editable: false,
      valueGetter: (params: GridValueGetterParams) => {
        const dateString = params.row.timestamp;
        const dateParts = dateString.split('.');
        const formattedDate = `${dateParts[1]}/${dateParts[0]}/${dateParts[2]}`;
        const parsedDate = new Date(formattedDate);
        return parsedDate;
      },
    },
    {
      field: 'fullName',
      headerName: 'Name',
      description: 'This column has a value getter and is not sortable.',
      sortable: false,
      editable: false,
      width: 160,
      valueGetter: (params: GridValueGetterParams) =>
        `${params.row.firstname || ''} ${params.row.lastname || ''}`,
    },
    {
      field: 'company',
      headerName: 'Firma',
      sortable: false,
      editable: false,
      width: 160,
    },
    {
      field: 'address',
      headerName: 'Adresse',
      sortable: true,
      editable: false,
      width: 160,
    },
    {
      field: 'orderProductsString',
      headerName: 'Artikel',
      width: 450,
      editable: false,
      sortable: false,
      filterable: false,
      disableExport: false,
      renderCell: cellValues => (
        <ul style={{ display: 'flex', flexDirection: 'column' }}>
          {cellValues.row.products.map(item => (
            <li key={item.id}>{getOrderItem(item)}</li>
          ))}
        </ul>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      editable: false,
      valueGetter: (params: GridValueGetterParams) =>
        creditStatusTranslation[params.row.status as RedeemCreditStatus],
    },
    {
      field: 'changeStatus',
      editable: false,
      sortable: false,
      filterable: false,
      disableExport: true,
      headerName: 'Status ändern',
      renderCell: cellValues => (
        <div>
          <IconButton
            aria-label="StatusOptions"
            onClick={event => {
              handleClick(event, cellValues.row);
            }}
          >
            <MoreHorizIcon />
          </IconButton>
          <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
            {Object.entries(creditStatusTranslation).map(([key, value]) => (
              <MenuItem
                key={key}
                onClick={() => updateRow(currentRow, key as RedeemCreditStatus)}
              >
                {value}
              </MenuItem>
            ))}
          </Menu>
        </div>
      ),
    },
  ];

  return (
    <Container sx={{ width: `calc(100% - ${240}px)`, ml: `${240}px` }}>
      <Box
        component="main"
        sx={{ flexGrow: 1, bgcolor: 'background.default', p: 3 }}
      >
        <Typography variant="h4">Bestellungen</Typography>
      </Box>
      <Box sx={{ height: '80vh', width: 1450 }}>
        <DataGrid
          getRowHeight={() => 'auto'}
          localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
          components={{
            Toolbar: () => (
              <GridToolbarContainer sx={{ justifyContent: 'flex-end' }}>
                <GridToolbarExport />
                <GridToolbarQuickFilter />
              </GridToolbarContainer>
            ),
          }}
          checkboxSelection={false}
          rows={allOrders}
          sortModel={[
            {
              field: 'timestamp',
              sort: 'desc',
            },
          ]}
          columns={columns}
          getRowId={row => row.id}
          initialState={{
            columns: {
              columnVisibilityModel: {
                orderProductsString: true,
              },
            },
            filter: {
              filterModel: {
                items: [
                  {
                    id: 'status_1',
                    field: 'status',
                    operator: 'isAnyOf',
                    value: ['Offen', 'In Bearbeitung', 'In Bearbeitung Extern'],
                  },
                ],
              },
            },
            pagination: {
              paginationModel: {
                pageSize: 25,
              },
            },
          }}
          pageSizeOptions={[25]}
          disableRowSelectionOnClick
        />
      </Box>
    </Container>
  );
});

export default MaexchenOrdersPage;
